/* eslint-disable max-lines */
import { useSendAudio } from '@hakimo-ui/hakimo/data-access';
import {
  LocationAlarmCamera,
  QuickResolveActionItem,
  Situation,
  SOPWorkflow,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { toast, trackEvent } from '@hakimo-ui/hakimo/util';
import { Button } from '@hakimo-ui/shared/ui-base';
import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import {
  getQuickResolveActionLabel,
  hasQuickResolveSubActions,
} from '../sop-manager/type-guards';
import { WorkflowActions } from './WorkflowActions';
import { WorkflowBody } from './WorkflowBody';
import {
  currentStepAtom,
  getLatestCameraWithTalkdown,
  getSOPSteps,
} from './util';

interface Props {
  navigateToResolve: (action?: QuickResolveActionItem) => void;
  navigateToEscalate: () => void;
  sopWorkflow: SOPWorkflow;
  locationAlarmId: string;
  alarmCameras: LocationAlarmCamera[];
  onQuickResolve: (comment: string) => void;
  isResolveSubmitting: boolean;
  selectedSituation: Situation | undefined;
  onUpdateSituation: (selectedSituation: Situation) => void;
  locationId: string;
  product?: UnifiedMonitoringProduct;
  alarmStartEpochMs?: number;
  alarmEndEpochMs?: number;
  locationTimezone?: string;
}

export function SOPWorkflowRoot(props: Props) {
  const {
    navigateToEscalate,
    navigateToResolve,
    sopWorkflow,
    alarmCameras,
    locationAlarmId,
    onQuickResolve,
    isResolveSubmitting,
    selectedSituation,
    onUpdateSituation,
    locationId,
    product,
    alarmStartEpochMs,
    alarmEndEpochMs,
    locationTimezone,
  } = props;

  const [currentStep, setCurrentStep] = useAtom(currentStepAtom);
  const [talkdownInitiated, setTalkdownInitiated] = useState(false);

  const latestCameraWithSpeaker = useMemo(() => {
    return getLatestCameraWithTalkdown(alarmCameras);
  }, [alarmCameras]);

  const navigateToNextStep = (selectedSituationVal?: Situation) => {
    setCurrentStep((prev) => prev + 1);
    setTalkdownInitiated(false);
    selectedSituationVal && onUpdateSituation(selectedSituationVal);
  };

  const logTalkdown = useCallback(
    (cameraId: string) => {
      trackEvent('submit_camera_details_audio_talkdown', {
        camera_id: cameraId,
        locationAlarmId,
      });
      setTalkdownInitiated(true);
    },
    [locationAlarmId]
  );

  const mutation = useSendAudio(
    latestCameraWithSpeaker ? latestCameraWithSpeaker.id : null,
    undefined,
    locationAlarmId,
    () => {
      logTalkdown(latestCameraWithSpeaker ? latestCameraWithSpeaker.id : '');
      toast('Audio sent to camera successfully');
    },
    undefined,
    product
  );

  const performTalkdown = useCallback(
    (file?: File) => {
      const formData = new FormData();
      formData.append('audio', file || '');
      mutation.mutate(formData);
    },
    [mutation]
  );

  const logAndEscalate = (selectedSituationVal?: Situation) => {
    const updatedSituation = selectedSituationVal ?? selectedSituation;
    updatedSituation && onUpdateSituation(updatedSituation);
    trackEvent('sop_workflow_escalate_location_alarm_step_count', {
      locationAlarmId,
      escalated_after_step_count: currentStep,
    });
    navigateToEscalate();
  };

  const items = getSOPSteps(
    navigateToNextStep,
    navigateToResolve,
    sopWorkflow,
    logAndEscalate,
    latestCameraWithSpeaker !== null
  );

  const currentItem = items[currentStep];

  const showQuestionAndAction = talkdownInitiated;

  const onQuickResolveAction = (action: QuickResolveActionItem) => () => {
    const hasSubActions = hasQuickResolveSubActions(action);

    if (hasSubActions) {
      // Action has subactions, navigate to resolve page with action
      navigateToResolve(action);
      return;
    }

    const actionText = getQuickResolveActionLabel(action);
    if (actionText.toLowerCase() === 'others') {
      navigateToResolve();
      return;
    }
    onQuickResolve(actionText);
  };

  const sopQuickResolveActions =
    currentStep >= 0 ? sopWorkflow?.quickResolveActions ?? [] : [];

  const quickResolveButtons = sopQuickResolveActions.map((action, i) => ({
    key: i as string | number,
    label: getQuickResolveActionLabel(action),
    onClick: onQuickResolveAction(action),
  }));

  return (
    <>
      <div className="relative w-full">
        <WorkflowBody
          sopItem={currentItem}
          cameraId={latestCameraWithSpeaker?.id || ''}
          cameraName={latestCameraWithSpeaker?.name || ''}
          logTalkdown={logTalkdown}
          performTalkdown={performTalkdown}
          isTalkdownInProcess={mutation.isLoading}
          locationAlarmId={locationAlarmId}
          locationId={locationId}
          alarmStartEpochMs={alarmStartEpochMs}
          alarmEndEpochMs={alarmEndEpochMs}
          locationTimezone={locationTimezone}
        />

        {showQuestionAndAction && (
          <>
            <div className="pt-4 font-bold">{currentItem.title}</div>
            <WorkflowActions
              sopItem={currentItem}
              locationAlarmId={locationAlarmId}
              stepNumber={currentStep}
            />
          </>
        )}
      </div>
      <div className="w-full pt-2">
        {quickResolveButtons.length > 0 && (
          <div className="space-y-2">
            <div className="text-onlight-text-1 dark:text-ondark-text-1 font-sans text-sm font-semibold">
              Quick Resolve
            </div>
            <div className="flex flex-wrap items-start justify-start gap-2">
              {quickResolveButtons.map(({ key, label, onClick }) => (
                <Button
                  key={key}
                  onClick={onClick}
                  disabled={isResolveSubmitting}
                  className="p-1 px-2"
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
